import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { paymentApi, memberApi } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

const PaymentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    member_id: '',
    amount: '',
    paid_on: new Date().toISOString().split('T')[0],
    valid_from: '',
    valid_until: ''
  });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calculatedPeriod, setCalculatedPeriod] = useState({
    valid_from: null,
    valid_until: null,
    months: 0
  });
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all members
        const membersResponse = await memberApi.getAll();
        setMembers(membersResponse.data);
        
        // If editing, fetch payment details
        if (isEditMode) {
          const paymentResponse = await paymentApi.getById(id);
          const payment = paymentResponse.data;
          
          setFormData({
            member_id: payment.member_id,
            amount: payment.amount,
            paid_on: payment.paid_on ? new Date(payment.paid_on).toISOString().split('T')[0] : '',
            valid_from: payment.valid_from ? new Date(payment.valid_from).toISOString().split('T')[0] : '',
            valid_until: payment.valid_until ? new Date(payment.valid_until).toISOString().split('T')[0] : ''
          });
        } else if (membersResponse.data.length > 0) {
          // Set default member if creating new payment
          setFormData(prev => ({
            ...prev,
            member_id: membersResponse.data[0].id
          }));
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, [id, isEditMode]);
  
  // Fetch selected member details when member_id changes
  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (!formData.member_id) return;
      
      try {
        const response = await memberApi.getById(formData.member_id);
        setSelectedMember(response.data);
      } catch (err) {
        console.error('Failed to fetch member details:', err);
      }
    };
    
    fetchMemberDetails();
  }, [formData.member_id]);
  
  // Calculate payment period when amount, paid_on, or selected member changes
  useEffect(() => {
    if (!selectedMember || !formData.amount || !formData.paid_on) {
      setCalculatedPeriod({
        valid_from: null,
        valid_until: null,
        months: 0
      });
      return;
    }
    
    // Get the member's monthly fee
    const feeHistory = selectedMember.monthly_fee_history || [];
    if (feeHistory.length === 0) {
      console.error('Member has no fee history');
      return;
    }
    
    // Sort fee history by valid_from date in descending order
    const sortedHistory = [...feeHistory].sort((a, b) => {
      return new Date(b.valid_from) - new Date(a.valid_from);
    });
    
    // Get the fee valid at the payment date
    const paymentDate = new Date(formData.paid_on);
    let monthlyFee = 0;
    
    for (const fee of sortedHistory) {
      const validFrom = new Date(fee.valid_from);
      if (validFrom <= paymentDate) {
        monthlyFee = fee.amount;
        break;
      }
    }
    
    if (monthlyFee <= 0) {
      console.error('Could not determine monthly fee');
      return;
    }
    
    // Calculate months covered
    const amount = parseFloat(formData.amount);
    const monthsExact = amount / monthlyFee;
    const monthsCovered = Math.round(monthsExact);
    
    if (monthsCovered <= 0) {
      console.error('Payment amount must cover at least one month');
      return;
    }
    
    // Calculate valid_from and valid_until
    const validFrom = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), 1);
    const validUntil = new Date(validFrom);
    validUntil.setMonth(validUntil.getMonth() + monthsCovered);
    validUntil.setDate(0); // Last day of the previous month
    
    setCalculatedPeriod({
      valid_from: validFrom,
      valid_until: validUntil,
      months: monthsCovered
    });
  }, [selectedMember, formData.amount, formData.paid_on]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string values to appropriate types and remove empty fields
      const processedData = {
        member_id: formData.member_id,
        amount: parseFloat(formData.amount),
        paid_on: formData.paid_on
      };
      
      // Use user-provided dates if available, otherwise use calculated period
      if (formData.valid_from) {
        processedData.valid_from = formData.valid_from;
      } else if (calculatedPeriod.valid_from) {
        processedData.valid_from = calculatedPeriod.valid_from.toISOString().split('T')[0];
      }
      
      if (formData.valid_until) {
        processedData.valid_until = formData.valid_until;
      } else if (calculatedPeriod.valid_until) {
        processedData.valid_until = calculatedPeriod.valid_until.toISOString().split('T')[0];
      }
      
      if (isEditMode) {
        await paymentApi.update(id, processedData);
      } else {
        await paymentApi.create(processedData);
      }
      navigate('/payments');
    } catch (err) {
      setError('Failed to save payment');
      console.error(err);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-3">{isEditMode ? 'Edit Payment' : 'Record New Payment'}</h1>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="member_id" className="form-label fw-bold">Member</label>
                  <select
                    className="form-select"
                    id="member_id"
                    name="member_id"
                    value={formData.member_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a member</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.username || 'Unknown Member'}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="amount" className="form-label fw-bold">Amount (€)</label>
                  <div className="input-group">
                    <span className="input-group-text">€</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="paid_on" className="form-label fw-bold">Payment Date</label>
                  <input
                    type="date"
                    className="form-control"
                    id="paid_on"
                    name="paid_on"
                    value={formData.paid_on}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <hr className="my-4" />
                <h5 className="mb-3">Validity Period (Optional)</h5>
                
                {calculatedPeriod.valid_from && calculatedPeriod.valid_until && (
                  <div className="alert alert-info mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-info-circle me-2 fs-5"></i>
                      <div>
                        <strong>Calculated Period:</strong> This payment will cover {calculatedPeriod.months} month{calculatedPeriod.months !== 1 ? 's' : ''} 
                        ({formatDate(calculatedPeriod.valid_from)} - {formatDate(calculatedPeriod.valid_until)})
                        {selectedMember && selectedMember.monthly_fee_history && selectedMember.monthly_fee_history.length > 0 && (
                          <div className="mt-1 small">
                            Based on monthly fee of {selectedMember.monthly_fee_history[0].amount}€
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          valid_from: calculatedPeriod.valid_from.toISOString().split('T')[0],
                          valid_until: calculatedPeriod.valid_until.toISOString().split('T')[0]
                        }));
                      }}
                    >
                      <i className="fas fa-magic me-1"></i>
                      Use Calculated Period
                    </button>
                  </div>
                )}
                
                <div className="mb-3">
                  <label htmlFor="valid_from" className="form-label">Valid From</label>
                  <input
                    type="date"
                    className="form-control"
                    id="valid_from"
                    name="valid_from"
                    value={formData.valid_from}
                    onChange={handleChange}
                  />
                  <div className="form-text">
                    If left empty, the system will calculate this based on the payment date (starting from the 1st of the month).
                  </div>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="valid_until" className="form-label">Valid Until</label>
                  <input
                    type="date"
                    className="form-control"
                    id="valid_until"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleChange}
                  />
                  <div className="form-text">
                    If left empty, the system will calculate this based on the payment amount and member's fee. 
                    For example, if the monthly fee is 15€ and the payment is 30€, it will cover 2 months.
                    Partial payments are rounded to the nearest month (e.g., 25€ for a 15€ fee covers 2 months).
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-success px-4">
                    <i className="fas fa-save me-2"></i>
                    {isEditMode ? 'Update Payment' : 'Record Payment'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/payments')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
